import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';
import * as actions from "../../../store/actions";
import {
  Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography, CircularProgress, Chip,
} from '@mui/material';
import { i18n } from '../../../translate/i18n';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import Spinner from '../../../components/Spinner/index';

export default function Frequency(props) {
  const dispatch = useDispatch();
  let textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [firstLoad, setFirstLoad] = useState(true);

  const fetchFrequencies = useCallback(
    () => dispatch(actions.fetchFrequency(page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
  );

  const frequency = useSelector(state => state.frequency.frequencies);
  const frequenciesFilter = useSelector(state => state.frequency.frequenciesFilter);
  const loading = useSelector(state => state.frequency.loading);

  const headRows = [
    { id: "name", numeric: false, disablePadding: false, label: i18n.t('headRowsFreq.name') },
    { id: "month", numeric: false, disablePadding: false, label: i18n.t('headRowsFreq.month') },
  ];

  useEffect(() => {
    if (!isEmpty(frequenciesFilter)) {
      setPage(frequenciesFilter.page);
      setLimit(frequenciesFilter.limit);
      setSearchText(frequenciesFilter.searchText);
      setOrder(frequenciesFilter.order);
      setOrderBy(frequenciesFilter.orderBy);
    }
    setFirstLoad(false);
  }, []);

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

  useEffect(() => {
    if (!firstLoad) {
      fetchFrequencies(page, limit, searchText, order, orderBy);
      setAlreadySerched(true);
    }
  }, [firstLoad, page, limit, order, orderBy]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchFrequencies(page, limit, searchText, order, orderBy);
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const selectRow = (e, id) => {
    props.history.push(`form-period/${id}`)
  }

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    tableRow: {
      cursor: 'pointer',
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center'
    },
    thumbUp: {
      color: "#349c34"
    },
    thumbDown: {
      color: "#d04545"
    },
    avatar: {
      width: 50,
      height: 50,
    },
    avatarSmallText: {
      fontSize: 12,
      width: 50,
      height: 50,
    },
    chip: {
      margin: 5,
    },
  }

  const setLabelFrequency = (onDemand, type, amount) => {
    if (onDemand) return i18n.t('setLabelFrequency.onDemand');

    if (type === 'month') {
      return amount === 1 ? `${amount}${i18n.t('setLabelFrequency.month')}` : `${amount} ${i18n.t('setLabelFrequency.months')}`
    } else if (type === 'day') {
      return amount === 1 ? `${amount} ${i18n.t('setLabelFrequency.day')}` : `${amount} ${i18n.t('setLabelFrequency.days')}`
    }
  }

  return <>
    <Container maxWidth="sm">
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleFrequency.title')}
          placeholder={i18n.t('tableTitleFrequency.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
          addRow={() => props.history.push(`/form-period/new`)}
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {frequency.rows.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('tableTitleFrequency.Typography')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody style={styles.opacityContainer}>
            {frequency.rows.map((col, index) => (
              <TableRow
                style={styles.tableRow}
                key={col._id}
                hover
                onClick={event => selectRow(event, col._id)}
              >
                <TableCell>{col.name}</TableCell>
                <TableCell><Chip color={col.onDemand ? 'primary' : 'default'} label={setLabelFrequency(col.onDemand, col.type, col.amount)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={frequency.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;
}